import React, { useCallback } from 'react'
import cx from 'classnames'
import { l, Icon, Checkbox } from '@manychat/manyui'

import { FieldType } from 'common/fields/entities/enums'
import { Field } from 'common/fields/entities/field'
import UserFieldPicker from 'common/fields/linking/components/UserFieldPicker'
import { FilterCondition } from 'common/filter/interfaces'
import {
  UserFieldDateTypes,
  DATE_FORMULA,
  DATE_TIME_FORMULA,
  FilterField,
} from 'common/filter/models/AudienceFilter/constants'
import { VAR_REGEX } from 'constants/RegExs'

import { OffsetControl } from './components/OffsetControl'
import { exportFormula } from './utils'

import cm from './DateFormula.module.css'

interface DateFormulaProps {
  condition: FilterCondition
  fieldDesc: Field
  onChange: (data: Partial<FilterCondition>, closePopover?: boolean) => void
  onDelayCheckBoxChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

/**
 * Get field data type from CUF/SUF/GUF.
 */
const getFieldType = (type: FieldType | FilterField) => {
  if (type === FilterField.CURRENT_DATETIME) {
    return FieldType.DATETIME
  }

  return type.slice(type.indexOf('_') + 1) as FieldType
}

export const DateFormula = ({
  condition,
  fieldDesc,
  onChange,
  onDelayCheckBoxChange,
}: DateFormulaProps) => {
  const parseValue = useCallback((): number | null => {
    const isFormula = UserFieldDateTypes.FORMULA in condition
    let value = condition.value as string

    if (isFormula && value) {
      const parsing = VAR_REGEX.exec(value)

      if (parsing) {
        value = parsing[1].replace('cuf_', '')
      }
    }

    return parseInt(value, 10) || null
  }, [condition])

  const handleComparableUserFieldChange = useCallback(
    (event: { target: { value: number; field: Field } }) => {
      const value = exportFormula(event.target)
      onChange({ value }, false)
    },
    [onChange],
  )

  return (
    <>
      <label className="text-secondary">{l.translate('Custom Field')}</label>
      <div className={cx('m-t-xxs m-b-md', cm.userFieldPicker)}>
        <UserFieldPicker
          allowedTypes={[getFieldType(fieldDesc.type)]}
          name="date_field_id"
          onChange={handleComparableUserFieldChange}
          value={parseValue()}
          ariaLabel=""
        />
      </div>
      <div className="d-flex align-center m-t-xxs">
        <Checkbox
          name="delay"
          checked={Boolean(condition.offset)}
          disabled={Boolean(parseValue())}
          onChange={onDelayCheckBoxChange}
          label={l.translate('Offset')}
        />
        <Icon.Question
          size={16}
          className="m-l-xxs pointer"
          data-title-at="right"
          data-title={l.translate(
            'Offset option helps you to shift date/time from Custom Field by a certain value',
          )}
        />
      </div>
      {condition.offset && (
        <div className="d-flex align-center m-t-md">
          <OffsetControl condition={condition} fieldDesc={fieldDesc} onChange={onChange} />
        </div>
      )}
    </>
  )
}
